import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import * as React from 'react';

import { useDeveloperApi } from './api';
import { CopyButton, describeError, ErrorAlert, Loading, mono, ToneChip } from './components';
import { OUTBOX_REASONS, smsState } from './labels';
import type { SandboxSms, SmsTestResult } from './smsTypes';
import type { DeveloperOutbound, DeveloperOutboundUpdate } from './types';

const E164 = /^\+[1-9]\d{6,14}$/;

const compact = (phone: string) => phone.replace(/[\s().-]/g, '');

/** The sandbox's own SMS number (DSEC D5; plan §6): "Set up SMS" buys one number and turns on chat,
 *  "Send test SMS" goes through the outbound mode and allowlist, "Remove" releases the number. Partner
 *  admins (developer.outbound_manage) act; everyone else reads. */
const MessagingPanel = ({
    outbound,
    canManage,
    onOutboundSave,
    onChanged,
}: {
    outbound: DeveloperOutbound;
    canManage: boolean;
    onOutboundSave: (update: DeveloperOutboundUpdate) => Promise<DeveloperOutbound>;
    onChanged?: () => void;
}) => {
    const api = useDeveloperApi();
    const [sms, setSms] = React.useState<SandboxSms | null>(null);
    const [loadError, setLoadError] = React.useState<unknown>(null);
    const [busy, setBusy] = React.useState<null | 'setup' | 'remove' | 'test' | 'allowlist'>(null);
    const [error, setError] = React.useState<unknown>(null);
    const [confirming, setConfirming] = React.useState(false);
    const [phone, setPhone] = React.useState('');
    const [result, setResult] = React.useState<SmsTestResult | null>(null);

    const load = React.useCallback(() => {
        setLoadError(null);
        return api.sms()
            .then((data) => {
                setSms(data);
                setPhone((current) => current || data.your_phone || '');
            })
            .catch(setLoadError);
    }, [api]);

    React.useEffect(() => {
        load();
    }, [load]);

    if (!sms) {
        return loadError ? <ErrorAlert error={loadError} onRetry={load} /> : <Loading />;
    }

    const run = async (what: 'setup' | 'remove', call: () => Promise<SandboxSms>) => {
        setBusy(what);
        setError(null);
        setResult(null);
        try {
            setSms(await call());
            onChanged?.();
        } catch (e) {
            setError(e);
            load();
        } finally {
            setBusy(null);
        }
    };

    const sendTest = async () => {
        setBusy('test');
        setError(null);
        setResult(null);
        try {
            setResult(await api.testSms(compact(phone)));
        } catch (e) {
            setError(e);
        } finally {
            setBusy(null);
        }
    };

    const testPhone = compact(phone);
    const listed = (outbound.allowlist?.phones || []).includes(testPhone);
    const mode = outbound.mode;
    const addToAllowlist = async () => {
        setBusy('allowlist');
        setError(null);
        try {
            await onOutboundSave({
                ...(mode === 'outbox' ? { mode: 'allowlist' as const } : {}),
                allowlist: {
                    domains: outbound.allowlist?.domains || [],
                    emails: outbound.allowlist?.emails || [],
                    phones: listed ? outbound.allowlist?.phones || [] : [...(outbound.allowlist?.phones || []), testPhone],
                },
                comment: 'Test SMS phone (Messaging card)',
            });
        } catch (e) {
            setError(e);
        } finally {
            setBusy(null);
        }
    };

    const state = smsState(sms.state);
    const on = sms.state === 'on';
    const unfinished = sms.state === 'setting_up' || sms.state === 'removing';

    return (
        <Box data-testid="messaging-panel">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2">SMS:</Typography>
                <ToneChip label={state.label} tone={state.tone} hint={state.hint} />
                {on && sms.number && (
                    <>
                        <Typography sx={mono} data-testid="sms-number">{sms.number}</Typography>
                        <CopyButton value={sms.number} label="number" />
                    </>
                )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {on
                    ? 'Texts go out from this number to the phones your outbound mode allows; replies and chat arrive in this sandbox.'
                    : 'Without a number of its own, every SMS and WhatsApp message of this sandbox is kept in the outbox.'}
            </Typography>

            {unfinished && (
                <Alert severity="warning" sx={{ mt: 1 }} data-testid="sms-unfinished">
                    {sms.state === 'setting_up' ? 'Setting up SMS did not finish.' : 'Removing SMS did not finish.'}
                    {sms.error ? ` ${sms.error}` : ''}
                    {sms.state === 'setting_up' ? ' Set it up again to continue, or remove it.' : ' Remove it again to finish.'}
                </Alert>
            )}
            {on && sms.us_registration?.note && (
                <Alert severity="info" sx={{ mt: 1 }} data-testid="sms-us-registration">{sms.us_registration.note}</Alert>
            )}
            {!sms.available && !on && !unfinished && sms.unavailable_reason && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="sms-unavailable">
                    {sms.unavailable_reason}
                </Typography>
            )}
            {error ? <Alert severity="error" sx={{ mt: 1 }}>{describeError(error)}</Alert> : null}

            {canManage && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                    {(sms.state === 'off' || sms.state === 'setting_up') && (
                        <Button
                            variant={sms.state === 'off' ? 'contained' : 'outlined'}
                            disabled={!sms.available || busy !== null}
                            onClick={() => run('setup', api.setUpSms)}
                            startIcon={busy === 'setup' ? <CircularProgress size={14} /> : undefined}
                        >
                            {sms.state === 'off' ? 'Set up SMS' : 'Continue setting up'}
                        </Button>
                    )}
                    {sms.state !== 'off' && (
                        <Button color="error" variant="outlined" disabled={busy !== null} onClick={() => setConfirming(true)}>
                            {sms.state === 'removing' ? 'Remove again' : 'Remove'}
                        </Button>
                    )}
                </Stack>
            )}
            {canManage && sms.state === 'off' && sms.available && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    Buys one phone number for this sandbox (in the country of your production number, else the US) and turns on its chat.
                </Typography>
            )}

            {canManage && on && (
                <Box sx={{ mt: 2 }} data-testid="sms-test">
                    <Typography variant="subtitle2">Send test SMS</Typography>
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        <TextField
                            size="small"
                            label="Phone"
                            placeholder="+14155550123"
                            value={phone}
                            onChange={(ev) => { setPhone(ev.target.value); setResult(null); }}
                            inputProps={{ 'aria-label': 'Phone for the test SMS' }}
                        />
                        <Button variant="outlined" disabled={busy !== null || !E164.test(testPhone)} onClick={sendTest}>
                            Send test SMS
                        </Button>
                    </Stack>
                    {E164.test(testPhone) && (mode === 'outbox' || (mode === 'allowlist' && !listed)) && (
                        <Alert
                            severity="info"
                            sx={{ mt: 1 }}
                            data-testid="sms-test-hint"
                            action={(
                                <Button color="inherit" size="small" disabled={busy !== null} onClick={addToAllowlist}>
                                    {mode === 'outbox' ? 'Switch to Allowlist and add it' : 'Add it to the allowlist'}
                                </Button>
                            )}
                        >
                            {mode === 'outbox'
                                ? 'The outbound mode is Outbox only: nothing is delivered, so a test SMS stays in the outbox.'
                                : `${testPhone} is not on the allowlist: a test SMS to it stays in the outbox.`}
                        </Alert>
                    )}
                    {result && (
                        <Alert severity={result.delivered ? 'success' : 'info'} sx={{ mt: 1 }} data-testid="sms-test-result">
                            {result.delivered
                                ? `Sent to ${result.phone}.`
                                : `Kept in the outbox: ${OUTBOX_REASONS[result.reason] || result.reason}.`}
                            {result.error ? ` ${result.error}` : ''}
                        </Alert>
                    )}
                </Box>
            )}

            <Dialog open={confirming} onClose={() => setConfirming(false)} aria-labelledby="sms-remove-title">
                <DialogTitle id="sms-remove-title">Remove SMS?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {sms.number ? `The number ${sms.number} is released and cannot be got back. ` : ''}
                        The sandbox’s chat service is deleted with its chat history. Its SMS and WhatsApp messages are kept in the outbox again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => { setConfirming(false); run('remove', api.removeSms); }}
                    >
                        Remove SMS
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MessagingPanel;
